import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createStripeCheckoutSession,
	getQuickbooksInvoiceData,
	submitPaymentReportOnSuccess,
} from '@/services/stripe';

export const useGetQuickbooksInvoiceData = (
	data: {
		invoiceID: string;
		clinicQBOId: string;
	}[],
	enabled = true,
) => {
	return useQuery({
		queryKey: ['QuickbooksInvoiceData', data],
		queryFn: () => getQuickbooksInvoiceData(data),
		enabled: enabled && data.length > 0,
		keepPreviousData: true,
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
		cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
	});
};

export const useGetStripeCheckoutSession = (
	onSuccess?: (data: {
		message: string;
		url: string | null;
		stripeSessionType: 'session' | 'link';
	}) => void,
	onError?: (errorMessage: string) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			invoiceRequestData: {
				clinicQBOId: string;
				invoiceNumber: string;
				clinicGUID: string;
				clinicState: string;
				bypassSurcharges: boolean;
			}[];
			userData: {
				email: string;
				name: string;
			};
			stripeSessionType: 'session' | 'link';
			paymentMethod: 'CREDIT_CARD' | 'BANK_ACCOUNT';
		}) => createStripeCheckoutSession(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['StripeCheckoutSession'] });
			onSuccess?.(data);
		},
		onError: (error: {
			response?: {
				data?: {
					error?: {
						message?: string | { message?: string };
					};
					message?: string;
				};
			};
		}) => {
			'Failed to create Stripe Checkout Session. Please try again.';
			const errorMessage =
				typeof error?.response?.data === 'object' &&
				typeof error?.response?.data?.message
					? error?.response?.data?.message || 'Failed to create Stripe Checkout Session. Please try again.'
					: typeof error?.response?.data?.error?.message === 'object'
						? error?.response?.data?.error?.message?.message ||
							'Failed to create Stripe Checkout Session. Please try again.'
						: error?.response?.data?.error?.message ||
							error?.response?.data?.message ||
							'Failed to create Stripe Checkout Session. Please try again.';
			onError?.(errorMessage);
		},
	});
};

export const useSubmitPaymentReportOnSuccess = (
	data: {
		session_id: string;
		otp: string;
		payment_log_id: string;
	},
	enabled = true,
	onError?: (errorMessage: string) => void,
) => {
	// Hook to submit payment report on success
	return useQuery({
		// Query key for caching and identifying the query
		queryKey: ['SubmitPaymentReportOnSuccess', data],
		// Function to submit the payment report on success
		queryFn: () => submitPaymentReportOnSuccess(data),
		onError: (error: {
			response?: {
				data?: {
					error?: {
						message?: string | { message?: string };
					};
				};
			};
		}) => {
			const errorMessage =
				typeof error?.response?.data?.error?.message === 'object'
					? error?.response?.data?.error?.message?.message ||
						'Failed to submit payment report. Please try again.'
					: error?.response?.data?.error?.message ||
						'Failed to submit payment report. Please try again.';
			toast.error(errorMessage);
			onError?.(errorMessage);
		},
		enabled,
		keepPreviousData: false,
	});
};
